/**
 * Smart Collection membership resolution — the pure core of
 * LARGE-SETS-QUERY-AND-SMART-SETS-PRD.md §4.2 (owner decision D2).
 *
 * Semantics: live membership = (rule matches, evaluated now) UNION (pinned)
 * MINUS (excluded). Evaluated on read, every time — there is no stored
 * "rule match" row anywhere (see migration 637's header). This module is the
 * only place that combines those three inputs into a membership list, so the
 * rule can never drift from what's actually rendered.
 *
 * Dependency-free except the PQL evaluator (also dependency-free) — no
 * React/Zustand, so it can be unit-tested directly and reused by any future
 * server-side evaluation path (PRD Phase 2).
 */

import { evaluateNode } from '../pql/evaluate';
import type { QueryNode } from '../pql/types';
import type { CollectionMembershipSource } from '../../types/prioritization/collections';

export interface ExplicitMembershipInput {
  itemId: string;
  source: CollectionMembershipSource;
}

export interface ResolveSmartMembershipParams<T> {
  /** Candidate items to evaluate the rule against — typically all active items. */
  items: T[];
  getId: (item: T) => string;
  getFieldValue: (item: T, field: string) => unknown;
  /**
   * The collection's bound rule. `undefined` = static collection (no rule —
   * membership is exactly the explicit rows, migration-316 behavior).
   * `null` = a smart collection whose rule matches everything.
   */
  ruleAst: QueryNode | null | undefined;
  /** The collection's persisted collection_memberships rows. */
  explicitMemberships: ExplicitMembershipInput[];
}

export interface SmartMembershipResult {
  /** The resolved, de-duplicated live membership. */
  itemIds: string[];
  /** Which of those came from the rule (empty set for a static collection). */
  ruleMatchIds: Set<string>;
  isSmart: boolean;
}

export function resolveSmartMembership<T>(
  params: ResolveSmartMembershipParams<T>,
): SmartMembershipResult {
  const { items, getId, getFieldValue, ruleAst, explicitMemberships } = params;

  const included = new Set(
    explicitMemberships.filter((m) => m.source !== 'excluded').map((m) => m.itemId),
  );
  const excluded = new Set(
    explicitMemberships.filter((m) => m.source === 'excluded').map((m) => m.itemId),
  );

  if (ruleAst === undefined) {
    // Static collection — excluded rows shouldn't exist here in practice
    // (there's no rule to exclude from), but honor them defensively so a
    // collection that was unbound from a rule doesn't resurrect items a user
    // explicitly excluded while it was smart.
    const itemIds = [...included].filter((id) => !excluded.has(id));
    return { itemIds, ruleMatchIds: new Set(), isSmart: false };
  }

  const ruleMatchIds = new Set(
    items
      .filter((item) => evaluateNode(ruleAst, (field) => getFieldValue(item, field)))
      .map(getId),
  );

  const union = new Set([...ruleMatchIds, ...included]);
  for (const id of excluded) union.delete(id);

  return { itemIds: [...union], ruleMatchIds, isSmart: true };
}

// ============================================================================
// Delta ledger — non-authoritative "N new since you last looked" (PRD §4.2)
// ============================================================================

export interface SightingInput {
  itemId: string;
  firstMatchedAt: Date;
}

export interface DeltaResult {
  newCount: number;
  newItemIds: string[];
}

/**
 * Which currently-matching items are "new" relative to `lastViewedAt`. No
 * baseline yet (never viewed) reports zero new — a first-ever view isn't a
 * delta, it's just the collection.
 */
export function computeDelta(
  currentMatchIds: Iterable<string>,
  sightings: SightingInput[],
  lastViewedAt: Date | null,
): DeltaResult {
  if (!lastViewedAt) return { newCount: 0, newItemIds: [] };

  const sightingByItem = new Map(sightings.map((s) => [s.itemId, s.firstMatchedAt]));
  const cutoff = lastViewedAt.getTime();

  const newItemIds = [...currentMatchIds].filter((id) => {
    const firstMatchedAt = sightingByItem.get(id);
    // A current match with no recorded sighting yet is itself new — the
    // sighting sync (findUnsightedMatches, below) just hasn't run for it.
    if (!firstMatchedAt) return true;
    return firstMatchedAt.getTime() > cutoff;
  });

  return { newCount: newItemIds.length, newItemIds };
}

/**
 * Which currently-matching items have no sighting row yet — the set the
 * store should upsert into collection_sightings with first_matched_at=now().
 * Called opportunistically on read (PRD §4.2: "no sync jobs").
 */
export function findUnsightedMatches(
  currentMatchIds: Iterable<string>,
  existingSightingItemIds: Iterable<string>,
): string[] {
  const existing = new Set(existingSightingItemIds);
  return [...currentMatchIds].filter((id) => !existing.has(id));
}
